// pages/reporteria/usuarios/ComportamientoRetencion.jsx
import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    DatePicker,
    Button,
    Space,
    Spin,
    Alert,
    Select,
    Badge,
    Typography,
    Divider,
    Progress,
    Empty,
    message,
    Tooltip,
    Tag,
    Table,
    Tabs
} from 'antd';
import {
    RiseOutlined,
    FallOutlined,
    UserOutlined,
    TeamOutlined,
    ReloadOutlined,
    DownloadOutlined,
    FilterOutlined,
    TrophyOutlined,
    DollarOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    StopOutlined,
    LineChartOutlined,
    UserAddOutlined,
    UserDeleteOutlined,
    HeartOutlined,
    FireOutlined,
    PieChartOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReporteriaService from '../../../../service/Reporteria';
import useAuthContext from '../../../../hooks/useAuthContext';
import { exportarLibro } from '../../../../utils/exportExcel';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const ComportamientoRetencion = () => {
    const { user } = useAuthContext();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('recurrencia');

    // Estados para cada tipo de análisis
    const [dataRetencion, setDataRetencion] = useState(null);
    const [dataRecurrencia, setDataRecurrencia] = useState(null);
    const [dataChurn, setDataChurn] = useState(null);
    const [dataLTV, setDataLTV] = useState(null);

    // Filtros
    const [fechas, setFechas] = useState([
        dayjs().subtract(6, 'month'),
        dayjs()
    ]);
    const [periodoRetencion, setPeriodoRetencion] = useState('mensual');
    const [mesesInactividad, setMesesInactividad] = useState(6);

    const cargarDatos = async (tab = activeTab) => {
        setLoading(true);
        try {
            const params = {
                fechaInicio: fechas[0].format('YYYY-MM-DD'),
                fechaFin: fechas[1].format('YYYY-MM-DD')
            };

            switch (tab) {
                case 'retencion':
                    const retencion = await ReporteriaService.GetAnalisisRetencion({
                        ...params,
                        periodoAnalisis: periodoRetencion
                    });
                    setDataRetencion(retencion);
                    break;

                case 'recurrencia':
                    const recurrencia = await ReporteriaService.GetAnalisisRecurrencia(params);
                    setDataRecurrencia(recurrencia);
                    break;

                case 'churn':
                    const churn = await ReporteriaService.GetAnalisisChurn({
                        mesesInactividad
                    });
                    setDataChurn(churn);
                    break;

                case 'ltv':
                    const ltv = await ReporteriaService.GetAnalisisLTV({
                        segmentar: true
                    });
                    setDataLTV(ltv);
                    break;

                default:
                    break;
            }

            message.success('Datos cargados exitosamente');
        } catch (error) {
            console.error('Error al cargar datos:', error);
            message.error('Error al cargar los datos. Por favor intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarDatos(activeTab);
    }, [activeTab]);

    const handleTabChange = (key) => {
        setActiveTab(key);
        cargarDatos(key);
    };

    // Función para formatear moneda
    const formatearMoneda = (monto) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(monto || 0);
    };

    const handleExportarExcel = async () => {
        const hayDatos = dataRecurrencia || dataRetencion || dataChurn || dataLTV;
        if (!hayDatos) { message.warning('No hay datos cargados para exportar'); return; }
        try {
            const hojas = [];

            if (dataRecurrencia) {
                const r = dataRecurrencia;
                hojas.push({
                    nombre: 'Recurrencia',
                    metricas: [
                        { metrica: 'General' },
                        { metrica: 'Total de usuarios', valor: r.resumen?.totalUsuarios ?? 0, tipo: 'numero' },
                        { metrica: 'Tasa de recurrencia', valor: r.resumen?.tasaRecurrencia ?? 0, tipo: 'porcentaje' },
                        { metrica: 'Usuarios nuevos' },
                        { metrica: 'Cantidad', valor: r.nuevos?.cantidad ?? 0, tipo: 'numero' },
                        { metrica: '% del total', valor: r.resumen?.porcentajeNuevos ?? 0, tipo: 'porcentaje' },
                        { metrica: 'Inscripciones promedio', valor: r.nuevos?.inscripcionesPromedio ?? 0, tipo: 'decimal' },
                        { metrica: 'Monto promedio', valor: r.nuevos?.montoPromedio ?? 0, tipo: 'moneda' },
                        { metrica: 'Monto total', valor: r.nuevos?.montoTotal ?? 0, tipo: 'moneda' },
                        { metrica: 'Usuarios recurrentes' },
                        { metrica: 'Cantidad', valor: r.recurrentes?.cantidad ?? 0, tipo: 'numero' },
                        { metrica: '% del total', valor: r.resumen?.porcentajeRecurrentes ?? 0, tipo: 'porcentaje' },
                        { metrica: 'Inscripciones promedio', valor: r.recurrentes?.inscripcionesPromedio ?? 0, tipo: 'decimal' },
                        { metrica: 'Monto promedio', valor: r.recurrentes?.montoPromedio ?? 0, tipo: 'moneda' },
                        { metrica: 'Monto total', valor: r.recurrentes?.montoTotal ?? 0, tipo: 'moneda' },
                    ],
                });

                if (dataRecurrencia.distribucionFrecuencia?.length) {
                    hojas.push({
                        nombre: 'Frecuencia',
                        columnas: [
                            { header: 'Segmento', key: 'segmento', tipo: 'texto', ancho: 28 },
                            { header: 'Usuarios', key: 'cantidad', tipo: 'numero', total: true },
                            { header: '% del total', key: 'porcentaje', tipo: 'porcentaje' },
                        ],
                        filas: dataRecurrencia.distribucionFrecuencia.map(d => ({
                            segmento: d.rango || d.segmento || d.frecuencia,
                            cantidad: d.cantidad,
                            porcentaje: d.porcentaje,
                        })),
                    });
                }
            }

            // Cohortes: la retención vive en `retencion.periodo_N.tasaRetencion`,
            // no en campos planos retencionP1/P3/P6 — con esos nombres la hoja
            // salía con ceros en todas las columnas.
            if (dataRetencion?.cohortes?.length) {
                hojas.push({
                    nombre: 'Retención por cohorte',
                    columnas: [
                        { header: 'Cohorte', key: 'cohorte', tipo: 'texto', ancho: 16 },
                        { header: 'Usuarios nuevos', key: 'usuariosNuevos', tipo: 'numero', total: true },
                        ...Array.from({ length: 6 }, (_, i) => ({
                            header: `P${i}`,
                            key: `p${i}`,
                            tipo: 'porcentaje',
                            ancho: 9,
                            resaltar: (c) => (c[`p${i}`] >= 70 ? 'ok' : c[`p${i}`] >= 40 ? 'aviso' : 'alerta'),
                        })),
                    ],
                    filas: dataRetencion.cohortes.map(c => {
                        const fila = {
                            cohorte: c.cohorte,
                            usuariosNuevos: c.usuariosNuevos ?? 0,
                        };
                        for (let i = 0; i < 6; i += 1) {
                            fila[`p${i}`] = c.retencion?.[`periodo_${i}`]?.tasaRetencion ?? 0;
                        }
                        return fila;
                    }),
                    notas: [
                        `Cohortes ${periodoRetencion}. P0 es el período de ingreso; P1 a P5, los períodos siguientes.`,
                        'Verde ≥ 70 % · ámbar 40-69 % · rojo < 40 %.',
                    ],
                });
            }

            if (dataChurn) {
                const c = dataChurn.resumen || {};
                hojas.push({
                    nombre: 'Churn',
                    metricas: [
                        { metrica: 'Estado de la base' },
                        { metrica: 'Usuarios activos', valor: c.usuariosActivos ?? 0, tipo: 'numero' },
                        { metrica: 'Usuarios en riesgo', valor: c.usuariosEnRiesgo ?? 0, tipo: 'numero' },
                        { metrica: 'Usuarios inactivos', valor: c.usuariosInactivos ?? 0, tipo: 'numero' },
                        { metrica: 'Tasas' },
                        { metrica: 'Tasa de retención', valor: c.tasaRetencion ?? 0, tipo: 'porcentaje' },
                        { metrica: 'Tasa de churn', valor: c.tasaChurn ?? 0, tipo: 'porcentaje' },
                        { metrica: 'Meses de inactividad analizados', valor: c.mesesInactividadAnalizado ?? 0, tipo: 'numero' },
                        { metrica: 'Impacto económico' },
                        { metrica: 'Valor perdido', valor: c.valorPerdido ?? 0, tipo: 'moneda' },
                    ],
                });

                if (dataChurn.topUsuariosEnRiesgo?.length) {
                    hojas.push({
                        nombre: 'Usuarios en riesgo',
                        columnas: [
                            { header: 'Usuario', key: 'nombre', tipo: 'texto', ancho: 32 },
                            { header: 'RUT', key: 'rut', tipo: 'texto' },
                            { header: 'Email', key: 'email', tipo: 'texto', ancho: 32 },
                            { header: 'Comuna', key: 'comuna', tipo: 'texto' },
                            { header: 'Inscripciones', key: 'totalInscripciones', tipo: 'numero', total: true },
                            { header: 'Valor total', key: 'montoTotal', tipo: 'moneda', total: true },
                            { header: 'Días inactivo', key: 'diasInactividad', tipo: 'numero' },
                            { header: 'Última inscripción', key: 'ultimaInscripcion', tipo: 'fecha' },
                        ],
                        filas: dataChurn.topUsuariosEnRiesgo.map(u => ({
                            nombre: u.usuarioPrincipal?.nombre,
                            rut: u.usuarioPrincipal?.rut,
                            email: u.usuarioPrincipal?.email,
                            comuna: u.usuarioPrincipal?.comuna,
                            totalInscripciones: u.totalInscripciones,
                            montoTotal: u.montoTotal,
                            diasInactividad: u.diasInactividad,
                            ultimaInscripcion: u.ultimaInscripcion,
                        })),
                        notas: ['Listado de contacto para retención, ordenado por valor.'],
                    });
                }
            }

            if (dataLTV?.resumen) {
                const s = dataLTV.segmentos;
                hojas.push({
                    nombre: 'LTV',
                    metricas: [
                        { metrica: 'Resumen' },
                        { metrica: 'LTV promedio', valor: dataLTV.resumen.ltvPromedio ?? 0, tipo: 'moneda' },
                        { metrica: 'LTV mediano', valor: dataLTV.resumen.ltvMediano ?? 0, tipo: 'moneda' },
                        { metrica: 'LTV total de la base', valor: dataLTV.resumen.ltvTotal ?? 0, tipo: 'moneda' },
                        { metrica: 'Usuarios analizados', valor: dataLTV.resumen.totalUsuarios ?? 0, tipo: 'numero' },
                        { metrica: 'Inscripciones promedio', valor: dataLTV.resumen.inscripcionesPromedio ?? 0, tipo: 'decimal' },
                        ...(s ? [
                            { metrica: 'Segmento bajo' },
                            { metrica: 'Usuarios', valor: s.bajo?.cantidad ?? 0, tipo: 'numero' },
                            { metrica: 'LTV promedio', valor: s.bajo?.ltvPromedio ?? 0, tipo: 'moneda' },
                            { metrica: 'Contribución al total', valor: s.bajo?.contribucionTotal ?? 0, tipo: 'porcentaje' },
                            { metrica: 'Segmento medio' },
                            { metrica: 'Usuarios', valor: s.medio?.cantidad ?? 0, tipo: 'numero' },
                            { metrica: 'LTV promedio', valor: s.medio?.ltvPromedio ?? 0, tipo: 'moneda' },
                            { metrica: 'Contribución al total', valor: s.medio?.contribucionTotal ?? 0, tipo: 'porcentaje' },
                            { metrica: 'Segmento alto' },
                            { metrica: 'Usuarios', valor: s.alto?.cantidad ?? 0, tipo: 'numero' },
                            { metrica: 'LTV promedio', valor: s.alto?.ltvPromedio ?? 0, tipo: 'moneda' },
                            { metrica: 'Contribución al total', valor: s.alto?.contribucionTotal ?? 0, tipo: 'porcentaje' },
                        ] : []),
                    ],
                });
            }

            // Top LTV: los usuarios vienen anidados en `usuarioPrincipal` y el
            // monto es `ltvTotal`; los nombres planos anteriores devolvían vacío.
            if (dataLTV?.topUsuarios?.length) {
                hojas.push({
                    nombre: 'Top LTV',
                    columnas: [
                        { header: '#', key: 'ranking', tipo: 'numero', ancho: 6 },
                        { header: 'Usuario', key: 'nombre', tipo: 'texto', ancho: 32 },
                        { header: 'RUT', key: 'rut', tipo: 'texto' },
                        { header: 'Email', key: 'email', tipo: 'texto', ancho: 32 },
                        { header: 'Comuna', key: 'comuna', tipo: 'texto' },
                        { header: 'LTV total', key: 'ltvTotal', tipo: 'moneda', total: true },
                        { header: 'LTV mensual', key: 'ltvMensual', tipo: 'moneda' },
                        { header: 'Inscripciones', key: 'totalInscripciones', tipo: 'numero', total: true },
                        { header: 'Meses activo', key: 'mesesActivo', tipo: 'numero' },
                    ],
                    filas: dataLTV.topUsuarios.map((u, i) => ({
                        ranking: i + 1,
                        nombre: u.usuarioPrincipal?.nombre,
                        rut: u.usuarioPrincipal?.rut,
                        email: u.usuarioPrincipal?.email,
                        comuna: u.usuarioPrincipal?.comuna,
                        ltvTotal: u.ltvTotal,
                        ltvMensual: u.ltvMensual,
                        totalInscripciones: u.totalInscripciones,
                        mesesActivo: u.mesesActivo,
                    })),
                });
            }

            await exportarLibro({
                archivo: 'comportamiento-y-retencion',
                titulo: 'Comportamiento y retención',
                subtitulo: `Reporte de usuarios · ${fechas[0].format('DD/MM/YYYY')} al ${fechas[1].format('DD/MM/YYYY')}`,
                usuario: user,
                filtros: {
                    'Desde': fechas[0].format('DD/MM/YYYY'),
                    'Hasta': fechas[1].format('DD/MM/YYYY'),
                    'Período de cohortes': periodoRetencion,
                },
                hojas,
            });
            message.success('Excel exportado correctamente');
        } catch (error) {
            console.error('Error exportando Excel:', error);
            message.error('Error al generar el Excel');
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <Title level={2} className="mb-2">
                    <RiseOutlined className="mr-2" />
                    Comportamiento y Retención de Usuarios
                </Title>
                <Text type="secondary">
                    Análisis de patrones de uso, retención y valor de usuarios
                </Text>
            </div>

            {/* Filtros Generales */}
            <Card className="mb-6 shadow-sm">
                <Row gutter={[16, 16]} align="middle">
                    {(activeTab === 'retencion' || activeTab === 'recurrencia') && (
                        <>
                            <Col xs={24} sm={12} md={10}>
                                <Space direction="vertical" className="w-full">
                                    <Text strong>
                                        <FilterOutlined className="mr-2" />
                                        Rango de Fechas:
                                    </Text>
                                    <RangePicker
                                        value={fechas}
                                        onChange={setFechas}
                                        format="DD/MM/YYYY"
                                        className="w-full"
                                        size="large"
                                    />
                                </Space>
                            </Col>
                        </>
                    )}

                    {activeTab === 'retencion' && (
                        <Col xs={24} sm={12} md={6}>
                            <Space direction="vertical" className="w-full">
                                <Text strong>Periodo de Análisis:</Text>
                                <Select
                                    value={periodoRetencion}
                                    onChange={setPeriodoRetencion}
                                    className="w-full"
                                    size="large"
                                >
                                    <Option value="mensual">Mensual</Option>
                                    <Option value="trimestral">Trimestral</Option>
                                </Select>
                            </Space>
                        </Col>
                    )}

                    {activeTab === 'churn' && (
                        <Col xs={24} sm={12} md={6}>
                            <Space direction="vertical" className="w-full">
                                <Text strong>Meses de Inactividad:</Text>
                                <Select
                                    value={mesesInactividad}
                                    onChange={setMesesInactividad}
                                    className="w-full"
                                    size="large"
                                >
                                    <Option value={3}>3 meses</Option>
                                    <Option value={6}>6 meses</Option>
                                    <Option value={12}>12 meses</Option>
                                </Select>
                            </Space>
                        </Col>
                    )}

                    <Col xs={24} sm={12} md={4}>
                        <Space className="w-full mt-6">
                            <Button
                                type="primary"
                                size="large"
                                icon={<ReloadOutlined />}
                                onClick={() => cargarDatos(activeTab)}
                                loading={loading}
                                style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
                            >
                                Actualizar
                            </Button>
                            <Tooltip title="Exportar datos de la pestaña activa">
                                <Button
                                    size="large"
                                    icon={<DownloadOutlined />}
                                    onClick={handleExportarExcel}
                                    disabled={!dataRecurrencia && !dataRetencion && !dataChurn && !dataLTV}
                                >
                                    Exportar Excel
                                </Button>
                            </Tooltip>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <Spin size="large" tip="Cargando análisis de comportamiento..." />
                </div>
            ) : (
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    size="large"
                    className="bg-white p-4 rounded-lg shadow-sm"
                >
                    {/* TAB 1: RECURRENCIA */}
                    <TabPane
                        tab={
                            <span>
                                <UserAddOutlined />
                                Recurrencia
                            </span>
                        }
                        key="recurrencia"
                    >
                        {dataRecurrencia ? (
                            <>
                                {/* KPIs Principales - Recurrencia */}
                                <Row gutter={[16, 16]} className="mb-6">
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <TeamOutlined className="mr-2" />
                                                        Total Usuarios
                                                    </span>
                                                }
                                                value={dataRecurrencia.resumen.totalUsuarios}
                                                valueStyle={{ color: '#0047bb', fontSize: '32px', fontWeight: 'bold' }}
                                            />
                                        </Card>
                                    </Col>

                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <UserAddOutlined className="mr-2" />
                                                        Usuarios Nuevos
                                                    </span>
                                                }
                                                value={dataRecurrencia.nuevos.cantidad}
                                                suffix={`(${dataRecurrencia.resumen.porcentajeNuevos}%)`}
                                                valueStyle={{ color: '#0047bb', fontSize: '28px', fontWeight: 'bold' }}
                                            />
                                            <Divider className="my-2" />
                                            <Text type="secondary" className="text-xs">
                                                {formatearMoneda(dataRecurrencia.nuevos.montoPromedio)} promedio
                                            </Text>
                                        </Card>
                                    </Col>

                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <HeartOutlined className="mr-2" />
                                                        Usuarios Recurrentes
                                                    </span>
                                                }
                                                value={dataRecurrencia.recurrentes.cantidad}
                                                suffix={`(${dataRecurrencia.resumen.porcentajeRecurrentes}%)`}
                                                valueStyle={{ color: '#722ed1', fontSize: '28px', fontWeight: 'bold' }}
                                            />
                                            <Divider className="my-2" />
                                            <Text type="secondary" className="text-xs">
                                                {formatearMoneda(dataRecurrencia.recurrentes.montoPromedio)} promedio
                                            </Text>
                                        </Card>
                                    </Col>

                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <FireOutlined className="mr-2" />
                                                        Tasa Recurrencia
                                                    </span>
                                                }
                                                value={dataRecurrencia.resumen.tasaRecurrencia}
                                                suffix="%"
                                                valueStyle={{ color: '#fa8c16', fontSize: '32px', fontWeight: 'bold' }}
                                            />
                                            <Divider className="my-2" />
                                            <Progress
                                                percent={parseFloat(dataRecurrencia.resumen.tasaRecurrencia)}
                                                strokeColor="#fa8c16"
                                                showInfo={false}
                                                size="small"
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Comparación Nuevos vs Recurrentes */}
                                <Row gutter={[16, 16]} className="mb-6">
                                    <Col xs={24} lg={12}>
                                        <Card
                                            title={
                                                <span className="text-lg font-semibold">
                                                    <UserAddOutlined className="mr-2 text-blue-500" />
                                                    Usuarios Nuevos
                                                </span>
                                            }
                                            className="shadow-sm"
                                            style={{ height: '100%' }}
                                        >
                                            <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                                                <Row gutter={[16, 16]}>
                                                    <Col span={12}>
                                                        <div className="text-center">
                                                            <Text type="secondary" className="text-xs block mb-2">Total</Text>
                                                            <Text strong style={{ fontSize: '36px', color: '#0047bb' }}>
                                                                {dataRecurrencia.nuevos.cantidad}
                                                            </Text>
                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className="text-center">
                                                            <Text type="secondary" className="text-xs block mb-2">% del Total</Text>
                                                            <Text strong style={{ fontSize: '36px', color: '#0047bb' }}>
                                                                {dataRecurrencia.resumen.porcentajeNuevos}%
                                                            </Text>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <Divider className="my-4" />

                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Inscripciones Promedio:</Text>
                                                        <Text strong style={{ fontSize: '18px' }}>
                                                            {dataRecurrencia.nuevos.inscripcionesPromedio}
                                                        </Text>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Monto Promedio:</Text>
                                                        <Text strong style={{ fontSize: '18px', color: '#0047bb' }}>
                                                            {formatearMoneda(dataRecurrencia.nuevos.montoPromedio)}
                                                        </Text>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Monto Total:</Text>
                                                        <Text strong style={{ fontSize: '20px', color: '#0047bb' }}>
                                                            {formatearMoneda(dataRecurrencia.nuevos.montoTotal)}
                                                        </Text>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>

                                    <Col xs={24} lg={12}>
                                        <Card
                                            title={
                                                <span className="text-lg font-semibold">
                                                    <HeartOutlined className="mr-2 text-purple-500" />
                                                    Usuarios Recurrentes
                                                </span>
                                            }
                                            className="shadow-sm"
                                            style={{ height: '100%' }}
                                        >
                                            <div className="p-6 bg-gradient-to-br from-purple-50 to-violet-100 rounded-xl">
                                                <Row gutter={[16, 16]}>
                                                    <Col span={12}>
                                                        <div className="text-center">
                                                            <Text type="secondary" className="text-xs block mb-2">Total</Text>
                                                            <Text strong style={{ fontSize: '36px', color: '#722ed1' }}>
                                                                {dataRecurrencia.recurrentes.cantidad}
                                                            </Text>
                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className="text-center">
                                                            <Text type="secondary" className="text-xs block mb-2">% del Total</Text>
                                                            <Text strong style={{ fontSize: '36px', color: '#722ed1' }}>
                                                                {dataRecurrencia.resumen.porcentajeRecurrentes}%
                                                            </Text>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <Divider className="my-4" />

                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Inscripciones Promedio:</Text>
                                                        <Text strong style={{ fontSize: '18px' }}>
                                                            {dataRecurrencia.recurrentes.inscripcionesPromedio}
                                                        </Text>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Monto Promedio:</Text>
                                                        <Text strong style={{ fontSize: '18px', color: '#722ed1' }}>
                                                            {formatearMoneda(dataRecurrencia.recurrentes.montoPromedio)}
                                                        </Text>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <Text type="secondary">Monto Total:</Text>
                                                        <Text strong style={{ fontSize: '20px', color: '#722ed1' }}>
                                                            {formatearMoneda(dataRecurrencia.recurrentes.montoTotal)}
                                                        </Text>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Distribución por Frecuencia */}
                                <Row gutter={[16, 16]}>
                                    <Col xs={24}>
                                        <Card
                                            title={
                                                <span className="text-lg font-semibold">
                                                    <LineChartOutlined className="mr-2 text-blue-500" />
                                                    Distribución por Frecuencia de Inscripciones
                                                </span>
                                            }
                                            className="shadow-sm"
                                        >
                                            <Row gutter={[16, 16]}>
                                                {dataRecurrencia.distribucionFrecuencia.map((item, index) => (
                                                    <Col xs={24} sm={12} md={6} key={index}>
                                                        <div className="p-4 bg-gray-50 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all">
                                                            <div className="flex justify-between items-center mb-3">
                                                                <Text strong>{item.frecuencia}</Text>
                                                                <Badge
                                                                    count={`${item.porcentaje}%`}
                                                                    style={{ backgroundColor: '#0047bb' }}
                                                                />
                                                            </div>
                                                            <Text strong style={{ fontSize: '32px', color: '#0047bb' }}>
                                                                {item.cantidad}
                                                            </Text>
                                                            <Text type="secondary" className="block text-xs mt-1">usuarios</Text>
                                                            <Progress
                                                                percent={parseFloat(item.porcentaje)}
                                                                strokeColor="#0047bb"
                                                                showInfo={false}
                                                                className="mt-2"
                                                            />
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        ) : (
                            <Empty description="No hay datos de recurrencia disponibles" />
                        )}
                    </TabPane>

                    {/* TAB 2: CHURN */}
                    <TabPane
                        tab={
                            <span>
                                <UserDeleteOutlined />
                                Churn (Abandono)
                            </span>
                        }
                        key="churn"
                    >
                        {dataChurn ? (
                            <>
                                {/* KPIs Principales - Churn */}
                                <Row gutter={[16, 16]} className="mb-6">
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <CheckCircleOutlined className="mr-2" />
                                                        Usuarios Activos
                                                    </span>
                                                }
                                                value={dataChurn.resumen.usuariosActivos}
                                                valueStyle={{ color: '#0047bb', fontSize: '32px', fontWeight: 'bold' }}
                                            />
                                            <Divider className="my-2" />
                                            <Progress
                                                percent={parseFloat(dataChurn.resumen.tasaRetencion)}
                                                strokeColor="#0047bb"
                                                showInfo={false}
                                                size="small"
                                            />
                                        </Card>
                                    </Col>

                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <WarningOutlined className="mr-2" />
                                                        En Riesgo
                                                    </span>
                                                }
                                                value={dataChurn.resumen.usuariosEnRiesgo}
                                                valueStyle={{ color: '#faad14', fontSize: '32px', fontWeight: 'bold' }}
                                            />
                                            <Divider className="my-2" />
                                            <Text type="secondary" className="text-xs">
                                                Requieren atención inmediata
                                            </Text>
                                        </Card>
                                    </Col>

                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <StopOutlined className="mr-2" />
                                                        Inactivos
                                                    </span>
                                                }
                                                value={dataChurn.resumen.usuariosInactivos}
                                                valueStyle={{ color: '#ff4d4f', fontSize: '32px', fontWeight: 'bold' }}
                                            />
                                            <Divider className="my-2" />
                                            <Text type="secondary" className="text-xs">
                                                +{dataChurn.resumen.mesesInactividadAnalizado} meses sin actividad
                                            </Text>
                                        </Card>
                                    </Col>

                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <FallOutlined className="mr-2" />
                                                        Tasa de Churn
                                                    </span>
                                                }
                                                value={dataChurn.resumen.tasaChurn}
                                                suffix="%"
                                                valueStyle={{
                                                    color: dataChurn.resumen.tasaChurn > 30 ? '#ff4d4f' : '#faad14',
                                                    fontSize: '32px',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                            <Divider className="my-2" />
                                            <Text type="danger" className="text-xs">
                                                Valor perdido: {formatearMoneda(dataChurn.resumen.valorPerdido)}
                                            </Text>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Distribución por Estado */}
                                <Row gutter={[16, 16]} className="mb-6">
                                    <Col xs={24}>
                                        <Card
                                            title={
                                                <span className="text-lg font-semibold">
                                                    <PieChartOutlined className="mr-2 text-blue-500" />
                                                    Distribución por Estado de Actividad
                                                </span>
                                            }
                                            className="shadow-sm"
                                        >
                                            <Row gutter={[16, 16]}>
                                                {dataChurn.distribucionEstado.map((item, index) => (
                                                    <Col xs={24} md={8} key={index}>
                                                        <div
                                                            className="p-6 rounded-xl"
                                                            style={{
                                                                backgroundColor: `${item.color}15`,
                                                                border: `2px solid ${item.color}30`
                                                            }}
                                                        >
                                                            <div className="flex justify-between items-center mb-4">
                                                                <Text strong style={{ fontSize: '18px' }}>
                                                                    {item.estado}
                                                                </Text>
                                                                <Badge
                                                                    count={`${item.porcentaje}%`}
                                                                    style={{
                                                                        backgroundColor: item.color,
                                                                        fontSize: '16px',
                                                                        padding: '4px 12px',
                                                                        height: 'auto'
                                                                    }}
                                                                />
                                                            </div>
                                                            <Text strong style={{ fontSize: '48px', color: item.color }}>
                                                                {item.cantidad}
                                                            </Text>
                                                            <Text type="secondary" className="block text-sm mt-1">usuarios</Text>
                                                            <Progress
                                                                percent={parseFloat(item.porcentaje)}
                                                                strokeColor={item.color}
                                                                showInfo={false}
                                                                className="mt-4"
                                                                strokeWidth={10}
                                                            />
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Análisis por Segmento */}
                                <Row gutter={[16, 16]} className="mb-6">
                                    <Col xs={24}>
                                        <Card
                                            title={
                                                <span className="text-lg font-semibold">
                                                    <TeamOutlined className="mr-2 text-purple-500" />
                                                    Análisis por Frecuencia de Inscripciones
                                                </span>
                                            }
                                            className="shadow-sm"
                                        >
                                            <Row gutter={[16, 16]}>
                                                {dataChurn.segmentosPorInscripciones.map((segmento, index) => (
                                                    <Col xs={24} md={8} key={index}>
                                                        <div className="p-4 bg-gray-50 rounded-lg">
                                                            <Text strong className="text-lg block mb-4">{segmento.segmento}</Text>
                                                            <div className="space-y-3">
                                                                <div className="flex justify-between items-center">
                                                                    <Text style={{ color: '#0047bb' }}>
                                                                        <CheckCircleOutlined className="mr-2" />
                                                                        Activos:
                                                                    </Text>
                                                                    <Text strong>{segmento.activos}</Text>
                                                                </div>
                                                                <div className="flex justify-between items-center">
                                                                    <Text style={{ color: '#faad14' }}>
                                                                        <WarningOutlined className="mr-2" />
                                                                        En Riesgo:
                                                                    </Text>
                                                                    <Text strong>{segmento.riesgo}</Text>
                                                                </div>
                                                                <div className="flex justify-between items-center">
                                                                    <Text style={{ color: '#ff4d4f' }}>
                                                                        <StopOutlined className="mr-2" />
                                                                        Inactivos:
                                                                    </Text>
                                                                    <Text strong>{segmento.inactivos}</Text>
                                                                </div>
                                                                <Divider className="my-2" />
                                                                <div className="flex justify-between items-center">
                                                                    <Text type="secondary">Total:</Text>
                                                                    <Text strong style={{ fontSize: '20px' }}>{segmento.total}</Text>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Top Usuarios en Riesgo */}
                                {dataChurn.topUsuariosEnRiesgo && dataChurn.topUsuariosEnRiesgo.length > 0 && (
                                    <Row gutter={[16, 16]}>
                                        <Col xs={24}>
                                            <Card
                                                title={
                                                    <span className="text-lg font-semibold">
                                                        <WarningOutlined className="mr-2 text-orange-500" />
                                                        Top 20 Usuarios en Riesgo (Por Valor)
                                                    </span>
                                                }
                                                className="shadow-sm"
                                            >
                                                <Table
                                                    dataSource={dataChurn.topUsuariosEnRiesgo}
                                                    rowKey="userId"
                                                    pagination={{ pageSize: 10 }}
                                                    scroll={{ x: 1500 }}
                                                    expandable={{
                                                        expandedRowRender: (record) => (
                                                            <div className="p-4 bg-gray-50 rounded">
                                                                <Row gutter={[16, 16]}>
                                                                    {/* USUARIO PRINCIPAL (QUIEN COMPRÓ) */}
                                                                    <Col xs={24}>
                                                                        <Card
                                                                            size="small"
                                                                            title={
                                                                                <span className="text-base font-semibold">
                                                                                    <UserOutlined className="mr-2 text-blue-500" />
                                                                                    Usuario Principal (Quien Compró)
                                                                                </span>
                                                                            }
                                                                            className="mb-3"
                                                                            style={{ borderLeft: '4px solid #0047bb' }}
                                                                        >
                                                                            <Row gutter={[16, 16]}>
                                                                                <Col xs={24} md={12}>
                                                                                    <div className="space-y-2">
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">Nombre:</Text>
                                                                                            <Text strong style={{ fontSize: '15px' }}>
                                                                                                {record.usuarioPrincipal.nombre}
                                                                                            </Text>
                                                                                        </div>
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">RUT:</Text>
                                                                                            <Text strong>{record.usuarioPrincipal.rut}</Text>
                                                                                        </div>
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">Comuna:</Text>
                                                                                            <Tag color="blue">{record.usuarioPrincipal.comuna}</Tag>
                                                                                        </div>
                                                                                    </div>
                                                                                </Col>
                                                                                <Col xs={24} md={12}>
                                                                                    <div className="space-y-2">
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">Email:</Text>
                                                                                            <Text strong>{record.usuarioPrincipal.email}</Text>
                                                                                        </div>
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">Teléfono:</Text>
                                                                                            <Text strong>{record.usuarioPrincipal.telefono}</Text>
                                                                                        </div>
                                                                                    </div>
                                                                                </Col>
                                                                            </Row>
                                                                        </Card>
                                                                    </Col>

                                                                    {/* ÚLTIMA INSCRIPCIÓN (PUEDE SER PARA ÉL O UN FAMILIAR) */}
                                                                    <Col xs={24} md={12}>
                                                                        <Card
                                                                            size="small"
                                                                            title={
                                                                                <span>
                                                                                    {record.ultimaInscripcionDetalle.inscritoPara === 'Usuario Principal'
                                                                                        ? <UserOutlined className="mr-2 text-blue-500" />
                                                                                        : <TeamOutlined className="mr-2 text-purple-500" />
                                                                                    }
                                                                                    Última Inscripción
                                                                                </span>
                                                                            }
                                                                            extra={
                                                                                <Tag color={record.ultimaInscripcionDetalle.inscritoPara === 'Usuario Principal' ? 'green' : 'purple'}>
                                                                                    {record.ultimaInscripcionDetalle.inscritoPara}
                                                                                </Tag>
                                                                            }
                                                                        >
                                                                            <div className="space-y-2">
                                                                                <div className="flex justify-between">
                                                                                    <Text type="secondary">Inscrito Para:</Text>
                                                                                    <Text strong style={{ fontSize: '14px' }}>
                                                                                        {record.ultimaInscripcionDetalle.nombreInscrito}
                                                                                    </Text>
                                                                                </div>
                                                                                {record.ultimaInscripcionDetalle.inscritoPara === 'Familiar' && (
                                                                                    <div className="flex justify-between">
                                                                                        <Text type="secondary">RUT Inscrito:</Text>
                                                                                        <Text>{record.ultimaInscripcionDetalle.rutInscrito}</Text>
                                                                                    </div>
                                                                                )}
                                                                                <Divider className="my-2" />
                                                                                <div className="flex justify-between">
                                                                                    <Text type="secondary">Taller:</Text>
                                                                                    <Text strong>{record.ultimaInscripcionDetalle.taller}</Text>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <Text type="secondary">Recinto:</Text>
                                                                                    <Text>{record.ultimaInscripcionDetalle.recinto}</Text>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <Text type="secondary">Plan:</Text>
                                                                                    <Tag color="blue">{record.ultimaInscripcionDetalle.plan}</Tag>
                                                                                </div>
                                                                                <div className="flex justify-between">
                                                                                    <Text type="secondary">Monto:</Text>
                                                                                    <Text strong style={{ color: '#0047bb' }}>
                                                                                        {formatearMoneda(record.ultimaInscripcionDetalle.monto)}
                                                                                    </Text>
                                                                                </div>
                                                                            </div>
                                                                        </Card>
                                                                    </Col>

                                                                    <Col xs={24} md={12}>
                                                                        <Card size="small" title="Estadísticas de Inscripciones">
                                                                            <Row gutter={16}>
                                                                                <Col span={8}>
                                                                                    <Statistic
                                                                                        title="Total"
                                                                                        value={record.totalInscripciones}
                                                                                        prefix={<TeamOutlined />}
                                                                                        valueStyle={{ fontSize: '20px' }}
                                                                                    />
                                                                                </Col>
                                                                                <Col span={8}>
                                                                                    <Statistic
                                                                                        title="Usuario Principal"
                                                                                        value={record.inscripcionesUsuarioPrincipal}
                                                                                        prefix={<UserOutlined />}
                                                                                        valueStyle={{ fontSize: '20px', color: '#0047bb' }}
                                                                                    />
                                                                                </Col>
                                                                                <Col span={8}>
                                                                                    <Statistic
                                                                                        title="Familiar"
                                                                                        value={record.inscripcionesFamiliar}
                                                                                        prefix={<TeamOutlined />}
                                                                                        valueStyle={{ fontSize: '20px', color: '#722ed1' }}
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                        </Card>
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        ),
                                                        rowExpandable: () => true,
                                                    }}
                                                    columns={[
                                                        {
                                                            title: 'Usuario Principal',
                                                            dataIndex: ['usuarioPrincipal', 'nombre'],
                                                            key: 'nombrePrincipal',
                                                            width: 200,
                                                            fixed: 'left',
                                                            render: (val) => (
                                                                <div>
                                                                    <Text strong className="block">{val || 'N/A'}</Text>
                                                                    <Text type="secondary" className="text-xs">Comprador</Text>
                                                                </div>
                                                            )
                                                        },
                                                        {
                                                            title: 'RUT Principal',
                                                            dataIndex: ['usuarioPrincipal', 'rut'],
                                                            key: 'rutPrincipal',
                                                            width: 130,
                                                        },
                                                        {
                                                            title: 'Comuna',
                                                            dataIndex: ['usuarioPrincipal', 'comuna'],
                                                            key: 'comuna',
                                                            width: 120,
                                                            render: (val) => <Tag color="blue">{val}</Tag>
                                                        },
                                                        {
                                                            title: 'Última Inscripción Para',
                                                            key: 'ultimaInscripcionPara',
                                                            width: 180,
                                                            render: (_, record) => (
                                                                <div>
                                                                    <Tag
                                                                        color={record.ultimaInscripcionDetalle.inscritoPara === 'Usuario Principal' ? 'green' : 'purple'}
                                                                        icon={record.ultimaInscripcionDetalle.inscritoPara === 'Usuario Principal'
                                                                            ? <UserOutlined />
                                                                            : <TeamOutlined />
                                                                        }
                                                                    >
                                                                        {record.ultimaInscripcionDetalle.inscritoPara}
                                                                    </Tag>
                                                                    <Text className="block text-xs mt-1">
                                                                        {record.ultimaInscripcionDetalle.nombreInscrito}
                                                                    </Text>
                                                                </div>
                                                            )
                                                        },
                                                        {
                                                            title: 'Total Inscripciones',
                                                            dataIndex: 'totalInscripciones',
                                                            key: 'totalInscripciones',
                                                            align: 'center',
                                                            width: 120,
                                                            render: (val) => <Badge count={val} style={{ backgroundColor: '#0047bb' }} />
                                                        },
                                                        {
                                                            title: 'Usuario Principal',
                                                            dataIndex: 'inscripcionesUsuarioPrincipal',
                                                            key: 'inscripcionesUsuarioPrincipal',
                                                            align: 'center',
                                                            width: 130,
                                                            render: (val) => (
                                                                <Tag color="blue" icon={<UserOutlined />}>
                                                                    {val}
                                                                </Tag>
                                                            )
                                                        },
                                                        {
                                                            title: 'Familiar',
                                                            dataIndex: 'inscripcionesFamiliar',
                                                            key: 'inscripcionesFamiliar',
                                                            align: 'center',
                                                            width: 100,
                                                            render: (val) => (
                                                                <Tag color="purple" icon={<TeamOutlined />}>
                                                                    {val}
                                                                </Tag>
                                                            )
                                                        },
                                                        {
                                                            title: 'Valor Total',
                                                            dataIndex: 'montoTotal',
                                                            key: 'montoTotal',
                                                            align: 'right',
                                                            width: 150,
                                                            render: (val) => (
                                                                <Text strong style={{ color: '#722ed1' }}>
                                                                    {formatearMoneda(val)}
                                                                </Text>
                                                            )
                                                        },
                                                        {
                                                            title: 'Días Inactivo',
                                                            dataIndex: 'diasInactividad',
                                                            key: 'diasInactividad',
                                                            align: 'center',
                                                            width: 120,
                                                            render: (val) => (
                                                                <Tag color="warning">{val} días</Tag>
                                                            )
                                                        },
                                                        {
                                                            title: 'Última Actividad',
                                                            dataIndex: 'ultimaInscripcion',
                                                            key: 'ultimaInscripcion',
                                                            width: 150,
                                                            render: (val) => dayjs(val).format('DD/MM/YYYY')
                                                        }
                                                    ]}
                                                />
                                            </Card>
                                        </Col>
                                    </Row>
                                )}
                            </>
                        ) : (
                            <Empty description="No hay datos de churn disponibles" />
                        )}
                    </TabPane>

                    {/* TAB 3: LTV */}
                    <TabPane
                        tab={
                            <span>
                                <DollarOutlined />
                                Lifetime Value
                            </span>
                        }
                        key="ltv"
                    >
                        {dataLTV ? (
                            <>
                                {/* KPIs Principales - LTV */}
                                <Row gutter={[16, 16]} className="mb-6">
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <DollarOutlined className="mr-2" />
                                                        LTV Promedio
                                                    </span>
                                                }
                                                value={dataLTV.resumen.ltvPromedio}
                                                prefix="$"
                                                valueStyle={{ color: '#0047bb', fontSize: '28px', fontWeight: 'bold' }}
                                            />
                                        </Card>
                                    </Col>

                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <LineChartOutlined className="mr-2" />
                                                        LTV Mediano
                                                    </span>
                                                }
                                                value={dataLTV.resumen.ltvMediano}
                                                prefix="$"
                                                valueStyle={{ color: '#0047bb', fontSize: '28px', fontWeight: 'bold' }}
                                            />
                                        </Card>
                                    </Col>

                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <TrophyOutlined className="mr-2" />
                                                        LTV Total
                                                    </span>
                                                }
                                                value={dataLTV.resumen.ltvTotal}
                                                prefix="$"
                                                valueStyle={{ color: '#722ed1', fontSize: '28px', fontWeight: 'bold' }}
                                            />
                                        </Card>
                                    </Col>

                                    <Col xs={24} sm={12} lg={6}>
                                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                                            <Statistic
                                                title={
                                                    <span className="text-gray-600 font-medium">
                                                        <TeamOutlined className="mr-2" />
                                                        Total Usuarios
                                                    </span>
                                                }
                                                value={dataLTV.resumen.totalUsuarios}
                                                valueStyle={{ color: '#fa8c16', fontSize: '28px', fontWeight: 'bold' }}
                                            />
                                            <Divider className="my-2" />
                                            <Text type="secondary" className="text-xs">
                                                {dataLTV.resumen.inscripcionesPromedio} inscripciones promedio
                                            </Text>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Segmentos de LTV */}
                                {dataLTV.segmentos && (
                                    <Row gutter={[16, 16]} className="mb-6">
                                        <Col xs={24}>
                                            <Card
                                                title={
                                                    <span className="text-lg font-semibold">
                                                        <TrophyOutlined className="mr-2 text-yellow-500" />
                                                        Segmentación por Valor de Usuario
                                                    </span>
                                                }
                                                className="shadow-sm"
                                            >
                                                <Row gutter={[16, 16]}>
                                                    <Col xs={24} md={8}>
                                                        <div className="p-6 bg-gradient-to-br from-red-50 to-orange-100 rounded-xl border-2 border-orange-200">
                                                            <div className="flex justify-between items-center mb-4">
                                                                <Text strong style={{ fontSize: '20px' }}>Valor Bajo</Text>
                                                                <Badge
                                                                    count={`${dataLTV.segmentos.bajo.porcentaje}%`}
                                                                    style={{ backgroundColor: '#ff7875' }}
                                                                />
                                                            </div>
                                                            <Text strong style={{ fontSize: '42px', color: '#ff4d4f' }}>
                                                                {dataLTV.segmentos.bajo.cantidad}
                                                            </Text>
                                                            <Text type="secondary" className="block text-sm mb-4">usuarios</Text>

                                                            <Divider className="my-3" />

                                                            <div className="space-y-2">
                                                                <div className="flex justify-between">
                                                                    <Text type="secondary" className="text-xs">LTV Promedio:</Text>
                                                                    <Text strong>{formatearMoneda(dataLTV.segmentos.bajo.ltvPromedio)}</Text>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <Text type="secondary" className="text-xs">Contribución:</Text>
                                                                    <Text strong>{dataLTV.segmentos.bajo.contribucionTotal}%</Text>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>

                                                    <Col xs={24} md={8}>
                                                        <div className="p-6 bg-gradient-to-br from-blue-50 to-cyan-100 rounded-xl border-2 border-blue-200">
                                                            <div className="flex justify-between items-center mb-4">
                                                                <Text strong style={{ fontSize: '20px' }}>Valor Medio</Text>
                                                                <Badge
                                                                    count={`${dataLTV.segmentos.medio.porcentaje}%`}
                                                                    style={{ backgroundColor: '#0047bb' }}
                                                                />
                                                            </div>
                                                            <Text strong style={{ fontSize: '42px', color: '#0047bb' }}>
                                                                {dataLTV.segmentos.medio.cantidad}
                                                            </Text>
                                                            <Text type="secondary" className="block text-sm mb-4">usuarios</Text>

                                                            <Divider className="my-3" />

                                                            <div className="space-y-2">
                                                                <div className="flex justify-between">
                                                                    <Text type="secondary" className="text-xs">LTV Promedio:</Text>
                                                                    <Text strong>{formatearMoneda(dataLTV.segmentos.medio.ltvPromedio)}</Text>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <Text type="secondary" className="text-xs">Contribución:</Text>
                                                                    <Text strong>{dataLTV.segmentos.medio.contribucionTotal}%</Text>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>

                                                    <Col xs={24} md={8}>
                                                        <div className="p-6 bg-gradient-to-br from-yellow-50 to-amber-100 rounded-xl border-2 border-yellow-200">
                                                            <div className="flex justify-between items-center mb-4">
                                                                <Text strong style={{ fontSize: '20px' }}>Valor Alto</Text>
                                                                <Badge
                                                                    count={`${dataLTV.segmentos.alto.porcentaje}%`}
                                                                    style={{ backgroundColor: '#faad14' }}
                                                                />
                                                            </div>
                                                            <Text strong style={{ fontSize: '42px', color: '#faad14' }}>
                                                                {dataLTV.segmentos.alto.cantidad}
                                                            </Text>
                                                            <Text type="secondary" className="block text-sm mb-4">usuarios</Text>

                                                            <Divider className="my-3" />

                                                            <div className="space-y-2">
                                                                <div className="flex justify-between">
                                                                    <Text type="secondary" className="text-xs">LTV Promedio:</Text>
                                                                    <Text strong>{formatearMoneda(dataLTV.segmentos.alto.ltvPromedio)}</Text>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <Text type="secondary" className="text-xs">Contribución:</Text>
                                                                    <Text strong>{dataLTV.segmentos.alto.contribucionTotal}%</Text>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        </Col>
                                    </Row>
                                )}

                                {/* Top Usuarios por LTV */}
                                {dataLTV.topUsuarios && dataLTV.topUsuarios.length > 0 && (
                                    <Row gutter={[16, 16]}>
                                        <Col xs={24}>
                                            <Card
                                                title={
                                                    <span className="text-lg font-semibold">
                                                        <TrophyOutlined className="mr-2 text-yellow-500" />
                                                        Top 20 Usuarios por Lifetime Value
                                                    </span>
                                                }
                                                className="shadow-sm"
                                            >
                                                <Table
                                                    dataSource={dataLTV.topUsuarios}
                                                    rowKey="userId"
                                                    pagination={{ pageSize: 10 }}
                                                    scroll={{ x: 1200 }}
                                                    expandable={{
                                                        expandedRowRender: (record) => (
                                                            <div className="p-4 bg-gray-50 rounded">
                                                                <Row gutter={[16, 16]}>
                                                                    {/* USUARIO PRINCIPAL (QUIEN COMPRÓ) */}
                                                                    <Col xs={24}>
                                                                        <Card
                                                                            size="small"
                                                                            title={
                                                                                <span className="text-base font-semibold">
                                                                                    <UserOutlined className="mr-2 text-blue-500" />
                                                                                    Usuario Principal
                                                                                </span>
                                                                            }
                                                                            className="mb-3"
                                                                            style={{ borderLeft: '4px solid #0047bb' }}
                                                                        >
                                                                            <Row gutter={[16, 16]}>
                                                                                <Col xs={24} md={12}>
                                                                                    <div className="space-y-2">
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">Nombre:</Text>
                                                                                            <Text strong style={{ fontSize: '15px' }}>
                                                                                                {record.usuarioPrincipal.nombre}
                                                                                            </Text>
                                                                                        </div>
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">RUT:</Text>
                                                                                            <Text strong>{record.usuarioPrincipal.rut}</Text>
                                                                                        </div>
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">Comuna:</Text>
                                                                                            <Tag color="blue">{record.usuarioPrincipal.comuna}</Tag>
                                                                                        </div>
                                                                                    </div>
                                                                                </Col>
                                                                                <Col xs={24} md={12}>
                                                                                    <div className="space-y-2">
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">Email:</Text>
                                                                                            <Text strong>{record.usuarioPrincipal.email}</Text>
                                                                                        </div>
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">Teléfono:</Text>
                                                                                            <Text strong>{record.usuarioPrincipal.telefono}</Text>
                                                                                        </div>
                                                                                        <div className="flex justify-between">
                                                                                            <Text type="secondary">Primera Inscripción:</Text>
                                                                                            <Text>{dayjs(record.primeraInscripcion).format('DD/MM/YYYY')}</Text>
                                                                                        </div>
                                                                                    </div>
                                                                                </Col>
                                                                            </Row>
                                                                        </Card>
                                                                    </Col>

                                                                    {/* ESTADÍSTICAS DE VALOR */}
                                                                    <Col xs={24}>
                                                                        <Card size="small" title="Análisis de Valor">
                                                                            <Row gutter={16}>
                                                                                <Col span={6}>
                                                                                    <Statistic
                                                                                        title="LTV Total"
                                                                                        value={record.ltvTotal}
                                                                                        prefix="$"
                                                                                        valueStyle={{ fontSize: '24px', color: '#0047bb' }}
                                                                                    />
                                                                                </Col>
                                                                                <Col span={6}>
                                                                                    <Statistic
                                                                                        title="LTV Mensual"
                                                                                        value={record.ltvMensual}
                                                                                        prefix="$"
                                                                                        valueStyle={{ fontSize: '24px', color: '#0047bb' }}
                                                                                    />
                                                                                </Col>
                                                                                <Col span={6}>
                                                                                    <Statistic
                                                                                        title="Inscripciones"
                                                                                        value={record.totalInscripciones}
                                                                                        prefix={<TeamOutlined />}
                                                                                        valueStyle={{ fontSize: '24px' }}
                                                                                    />
                                                                                </Col>
                                                                                <Col span={6}>
                                                                                    <Statistic
                                                                                        title="Meses Activo"
                                                                                        value={record.mesesActivo}
                                                                                        suffix="meses"
                                                                                        valueStyle={{ fontSize: '24px', color: '#722ed1' }}
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                        </Card>
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        ),
                                                        rowExpandable: () => true,
                                                    }}
                                                    columns={[
                                                        {
                                                            title: 'Ranking',
                                                            key: 'ranking',
                                                            width: 80,
                                                            align: 'center',
                                                            fixed: 'left',
                                                            render: (_, __, index) => (
                                                                <Badge
                                                                    count={index + 1}
                                                                    style={{
                                                                        backgroundColor: index === 0 ? '#faad14' :
                                                                            index === 1 ? '#d9d9d9' :
                                                                                index === 2 ? '#cd7f32' : '#8c8c8c'
                                                                    }}
                                                                />
                                                            )
                                                        },
                                                        {
                                                            title: 'Usuario Principal',
                                                            key: 'usuarioPrincipal',
                                                            width: 200,
                                                            fixed: 'left',
                                                            render: (_, record) => (
                                                                <div>
                                                                    <Text strong className="block">{record.usuarioPrincipal.nombre}</Text>
                                                                    <Text type="secondary" className="text-xs">{record.usuarioPrincipal.email}</Text>
                                                                </div>
                                                            )
                                                        },
                                                        {
                                                            title: 'RUT',
                                                            dataIndex: ['usuarioPrincipal', 'rut'],
                                                            key: 'rut',
                                                            width: 130,
                                                        },
                                                        {
                                                            title: 'Comuna',
                                                            dataIndex: ['usuarioPrincipal', 'comuna'],
                                                            key: 'comuna',
                                                            width: 120,
                                                            render: (val) => <Tag color="blue">{val}</Tag>
                                                        },
                                                        {
                                                            title: 'LTV Total',
                                                            dataIndex: 'ltvTotal',
                                                            key: 'ltvTotal',
                                                            align: 'right',
                                                            width: 150,
                                                            render: (val) => (
                                                                <Text strong style={{ color: '#0047bb', fontSize: '16px' }}>
                                                                    {formatearMoneda(val)}
                                                                </Text>
                                                            )
                                                        },
                                                        {
                                                            title: 'LTV Mensual',
                                                            dataIndex: 'ltvMensual',
                                                            key: 'ltvMensual',
                                                            align: 'right',
                                                            width: 150,
                                                            render: (val) => formatearMoneda(val)
                                                        },
                                                        {
                                                            title: 'Inscripciones',
                                                            dataIndex: 'totalInscripciones',
                                                            key: 'totalInscripciones',
                                                            align: 'center',
                                                            width: 120,
                                                            render: (val) => <Badge count={val} style={{ backgroundColor: '#0047bb' }} />
                                                        },
                                                        {
                                                            title: 'Meses Activo',
                                                            dataIndex: 'mesesActivo',
                                                            key: 'mesesActivo',
                                                            align: 'center',
                                                            width: 120,
                                                            render: (val) => <Tag color="purple">{val} meses</Tag>
                                                        }
                                                    ]}
                                                />
                                            </Card>
                                        </Col>
                                    </Row>
                                )}
                            </>
                        ) : (
                            <Empty description="No hay datos de LTV disponibles" />
                        )}
                    </TabPane>

                    {/* TAB 4: RETENCIÓN (COHORTES) */}
                    <TabPane
                        tab={
                            <span>
                                <LineChartOutlined />
                                Retención (Cohortes)
                            </span>
                        }
                        key="retencion"
                    >
                        {dataRetencion ? (
                            <>
                                {/* KPIs - Retención */}
                                <Row gutter={[16, 16]} className="mb-6">
                                    <Col xs={24} sm={12} lg={8}>
                                        <Card className="shadow-sm">
                                            <Statistic
                                                title="Total Cohortes Analizadas"
                                                value={dataRetencion.resumen.totalCohortes}
                                                prefix={<CalendarOutlined />}
                                                valueStyle={{ color: '#0047bb' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={8}>
                                        <Card className="shadow-sm">
                                            <Statistic
                                                title="Retención Promedio (Periodo 1)"
                                                value={dataRetencion.resumen.promedioRetencionP1}
                                                suffix="%"
                                                valueStyle={{ color: '#0047bb' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={8}>
                                        <Card className="shadow-sm">
                                            <Statistic
                                                title="Total Usuarios Analizados"
                                                value={dataRetencion.resumen.totalUsuariosAnalizados}
                                                prefix={<TeamOutlined />}
                                                valueStyle={{ color: '#722ed1' }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Tabla de Cohortes */}
                                <Card
                                    title={
                                        <span className="text-lg font-semibold">
                                            <LineChartOutlined className="mr-2 text-blue-500" />
                                            Análisis de Retención por Cohorte
                                        </span>
                                    }
                                    className="shadow-sm"
                                >
                                    <Alert
                                        message="Interpretación"
                                        description="Cada fila representa una cohorte de usuarios que se inscribieron en el mismo periodo. Los porcentajes muestran cuántos usuarios de esa cohorte permanecieron activos en periodos posteriores."
                                        type="info"
                                        showIcon
                                        className="mb-4"
                                    />
                                    <Table
                                        dataSource={dataRetencion.cohortes}
                                        rowKey="cohorte"
                                        scroll={{ x: 1200 }}
                                        pagination={{ pageSize: 10 }}
                                        columns={[
                                            {
                                                title: 'Cohorte',
                                                dataIndex: 'cohorte',
                                                key: 'cohorte',
                                                fixed: 'left',
                                                width: 120,
                                                render: (val) => <Text strong>{val}</Text>
                                            },
                                            {
                                                title: 'Usuarios Nuevos',
                                                dataIndex: 'usuariosNuevos',
                                                key: 'usuariosNuevos',
                                                width: 130,
                                                align: 'center',
                                                render: (val) => <Badge count={val} style={{ backgroundColor: '#0047bb' }} />
                                            },
                                            ...Array.from({ length: 6 }, (_, i) => ({
                                                title: `P${i}`,
                                                key: `periodo_${i}`,
                                                width: 90,
                                                align: 'center',
                                                render: (record) => {
                                                    const tasa = record.retencion[`periodo_${i}`]?.tasaRetencion || 0;
                                                    const color = tasa >= 70 ? '#0047bb' :
                                                        tasa >= 40 ? '#faad14' : '#ff4d4f';
                                                    return (
                                                        <Tag color={color}>
                                                            {tasa.toFixed(1)}%
                                                        </Tag>
                                                    );
                                                }
                                            }))
                                        ]}
                                    />
                                </Card>
                            </>
                        ) : (
                            <Empty description="No hay datos de retención disponibles" />
                        )}
                    </TabPane>
                </Tabs>
            )}
        </div>
    );
};

export default ComportamientoRetencion;
